import React, { useEffect, useState } from 'react';
import * as estoqueApi from '../../api/estoque';
import * as produtosApi from '../../api/produtos';
import * as fornecedoresApi from '../../api/fornecedores';
import { useExigeEmpresaEspecifica } from '../../hooks/useExigeEmpresaEspecifica';
import AvisoSelecioneEmpresa from '../../components/common/AvisoSelecioneEmpresa';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import Skeleton from '../../components/common/Skeleton';
import SearchableSelect from '../../components/common/SearchableSelect';
import { usePaginacao } from '../../hooks/usePaginacao';
import { useSortable } from '../../hooks/useSortable';
import { formatDate } from '../../utils/date';

export default function Estoque() {
  const { bloqueado, carregandoEmpresas, semEmpresaCadastrada } = useExigeEmpresaEspecifica();
  const [itens, setItens] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [idProduto, setIdProduto] = useState('');
  const [idFornecedor, setIdFornecedor] = useState('');
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (bloqueado) return;
    Promise.all([produtosApi.listProdutos(), fornecedoresApi.listFornecedores()])
      .then(([p, f]) => {
        setProdutos(p.data);
        setFornecedores(f.data);
      })
      .catch(() => setErro('Erro ao carregar filtros de produto/fornecedor.'));
  }, [bloqueado]);

  useEffect(() => {
    if (bloqueado) {
      setLoading(false);
      return;
    }
    setLoading(true);
    estoqueApi
      .listEstoque({ id_produto: idProduto || undefined, id_fornecedor: idFornecedor || undefined })
      .then((r) => setItens(r.data))
      .catch(() => setErro('Erro ao carregar estoque.'))
      .finally(() => setLoading(false));
  }, [bloqueado, idProduto, idFornecedor]);

  const { sorted, Th } = useSortable(itens, 'produto_nome');
  const { paginados, Paginacao } = usePaginacao(sorted);

  if (carregandoEmpresas) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader titulo="Estoque" subtitulo="Saldo atual por lote e qualidade" />

      {erro && (
        <div className="sf-login-alert mb-3">
          <i className="bi bi-exclamation-circle" />
          {erro}
        </div>
      )}

      {bloqueado ? (
        <AvisoSelecioneEmpresa semEmpresaCadastrada={semEmpresaCadastrada} />
      ) : (
        <div className="sf-table-card">
          <div className="sf-table-toolbar flex-wrap">
            <div style={{ minWidth: 220 }}>
              <SearchableSelect
                options={produtos.map((p) => ({ value: p.id_produto, label: p.nome }))}
                value={idProduto}
                onChange={setIdProduto}
                placeholder="Filtrar por produto..."
              />
            </div>
            <div style={{ minWidth: 220 }}>
              <SearchableSelect
                options={fornecedores.map((f) => ({ value: f.id_fornecedor, label: f.nome }))}
                value={idFornecedor}
                onChange={setIdFornecedor}
                placeholder="Filtrar por fornecedor..."
              />
            </div>
            {(idProduto || idFornecedor) && (
              <button type="button" className="sf-btn sf-btn-ghost" onClick={() => { setIdProduto(''); setIdFornecedor(''); }}>
                <i className="bi bi-x-lg" />
                Limpar filtros
              </button>
            )}
          </div>

          {loading ? (
            <div className="p-4">
              <Skeleton linhas={6} />
            </div>
          ) : paginados.length === 0 ? (
            <EmptyState icon="bi-boxes" titulo="Nenhum item em estoque" texto="Ajuste os filtros ou registre uma nova entrada." acaoLabel="Registrar entrada" acaoTo="/lotes/novo" acaoIcon="bi-truck" />
          ) : (
            <>
              <div className="table-responsive">
                <table className="sf-table">
                  <thead>
                    <tr>
                      <Th campo="produto_nome">Produto</Th>
                      <Th campo="qualidade_nome">Qualidade</Th>
                      <Th campo="fornecedor_nome">Fornecedor</Th>
                      <th>Lote</th>
                      <th>Entrada</th>
                      <Th campo="saldo_atual">Saldo</Th>
                      <th>Unidade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginados.map((i) => (
                      <tr key={i.id_item_lote}>
                        <td>
                          <span className="d-flex align-items-center gap-2">
                            <span className="sf-cell-icon"><i className="bi bi-box-seam" /></span>
                            <span className="sf-cell-title">{i.produto_nome}</span>
                          </span>
                        </td>
                        <td>{i.qualidade_nome}</td>
                        <td>{i.fornecedor_nome}</td>
                        <td>{i.lote_codigo}</td>
                        <td>{formatDate(i.data_entrada)}</td>
                        <td>
                          <span style={Number(i.saldo_atual) <= 10 ? { color: 'var(--sf-danger-fg)', fontWeight: 700 } : { fontWeight: 700 }}>
                            {Number(i.saldo_atual)}
                          </span>
                        </td>
                        <td>{i.unidade_sigla}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Paginacao />
            </>
          )}
        </div>
      )}
    </div>
  );
}
